import type { ToastIcon } from 'react-toastify';
import { createElement, useCallback } from 'react';
import { toast as toastify } from 'react-toastify';
import type { AlertType } from '@/ui/components/Alert/Alert';
import type { VectorName } from '@/ui/components/VectorGraphic/VectorGraphic';
import Alert from '@/ui/components/Alert/Alert';

const getIcon = (type: AlertType): VectorName => {
  switch (type) {
    case 'success':
      return 'icon-circle-check';
    case 'error':
      return 'icon-circle-exclamation';
    case 'warning':
      return 'icon-triangle-exclamation';
    case 'info':
    default:
      return 'icon-info';
  }
};

export type ToastOptions = {
  autoClose?: number | false;
  closeButton?: boolean;
  closeOnClick?: boolean;
  icon?: ToastIcon;
};

const useToast = () => {
  const showToast = useCallback(
    (type: AlertType, message: string, options: ToastOptions = {}) => {
      const {
        autoClose = 5000,
        closeOnClick = true,
        closeButton = false,
        icon = false,
      } = options;

      return toastify(
        () =>
          createElement(Alert, {
            type,
            icon: getIcon(type),
            message,
          }),
        {
          type,
          autoClose,
          closeButton,
          closeOnClick,
          icon,
          hideProgressBar: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    },
    [],
  );

  const success = useCallback(
    (message: string, options?: ToastOptions) =>
      showToast('success', message, options),
    [showToast],
  );

  const error = useCallback(
    (message: string, options?: ToastOptions) =>
      showToast('error', message, options),
    [showToast],
  );

  const warning = useCallback(
    (message: string, options?: ToastOptions) =>
      showToast('warning', message, options),
    [showToast],
  );

  const info = useCallback(
    (message: string, options?: ToastOptions) =>
      showToast('info', message, options),
    [showToast],
  );

  return {
    success,
    error,
    warning,
    info,
  };
};

export default useToast;
